import * as Sentry from '@sentry/browser';
import Vue from 'vue';
import Popper from 'vue-popperjs';
import { mapGetters } from 'vuex';
import { filesize } from 'src/utils/files';
import Progressbar from 'src/components/widgets/Progressbar.vue';
import Project from 'src/api/project';
import { CLEAR_REQUEST_ERRORS } from 'src/store/types';
import FormatUtils from 'src/utils/formatters';
const TIMER_DELAY = 2500;
const INDEXING_STATUS = 'INDEXING';
const ERROR_STATUS = 'ERROR';
// See sc-26125 for why we allow csv\d+
const FILE_TYPE_RE = /(csv|xls|xlsx|mp3|wav|mp4|m4v|flac|m4a|csv\d+)$/i;
const DataUpload = Vue.extend({
    components: { Popper, Progressbar },
    props: {
        addData: { type: Boolean, default: false },
        fileRowLimit: { type: Number, required: true },
        fileColumnLimit: { type: Number, required: true },
        projectRowLimit: { type: Number, required: false, default: null },
        projectColumnLimit: { type: Number, required: false, default: null },
    },
    data() {
        return {
            dataFile: null,
            dataUnitsError: null,
            fileName: '',
            fileProgress: undefined,
            fileSelected: false,
            fileSize: 0,
            fileSizeError: null,
            fileTypeError: null,
            headers: null,
            invalidFileType: false,
            numResponses: 0,
            samples: null,
            uploadFinished: false,
            uploadError: false,
            uploadErrorDetails: null,
            uploadRequest: null,
            checkFileStatusRequest: null,
        };
    },
    computed: {
        ...mapGetters([
            'currentUser', 'integrations', 'featureFlags',
        ]),
        isAlive() {
            return !this._isDestroyed && !this._isBeingDestroyed;
        },
        hasIntegrations() {
            return this.integrations && Object.keys(this.integrations).length > 0;
        },
        hasMoreRowsThanColumns() {
            return this.numResponses >= this.dataFile?.schema?.length ?? 0;
        },
        numEmptyHeaders() {
            return this.headers?.filter(h => h.length === 0).length;
        },
        allowedExtensions() {
            return '.csv,.csv*,.xls,.xlsx,.mp3,.wav,.mp4,.m4v,.flac';
        }
    },
    beforeDestroy() {
        this.cancelUpload();
    },
    methods: {
        formatNumber: FormatUtils.number,
        clearUpload() {
            if (this.periodicTask) {
                clearTimeout(this.periodicTask);
            }
            this.resetFile();
        },
        // Check the status of the uploaded file.
        // This method continually polls the file status until it is ready for indexing.
        async checkFileStatus() {
            if (!this.dataFile)
                return;
            try {
                const result = await Project.fetchFileStatus(this.dataFile.id, request => {
                    this.checkFileStatusRequest = request;
                });
                if (!this.isAlive)
                    return;
                if (this.dataFile.id !== result?.id)
                    return;
                this.dataFile = result;
                if (this.dataFile.status === INDEXING_STATUS) {
                    // File has finished processing and ready for indexing
                    this.periodicTask = null;
                    this.getDataSample();
                    this.$analytics.track.project.uploadFile(result.file_size, result.file_name, result.records, result.schema.length);
                }
                else if (this.dataFile.status !== ERROR_STATUS) {
                    this.periodicTask = setTimeout(this.checkFileStatus, TIMER_DELAY);
                }
                else if (this.dataFile.status === ERROR_STATUS) {
                    if (this.dataFile.status_text.toLowerCase().indexOf('need more data units') >= 0) {
                        this.dataUnitsError = this.dataFile.status_text;
                        this.resetFile();
                    }
                    else {
                        this.uploadError = true;
                        this.uploadErrorDetails = this.dataFile.status_text;
                    }
                }
            }
            catch (errors) {
                if (!this.isAlive)
                    return;
                this.$store.dispatch(CLEAR_REQUEST_ERRORS); // use custom instead of global error handling
                this.uploadError = true;
            }
            finally {
                this.checkFileStatusRequest = null;
            }
        },
        // Get sample of the data
        async getDataSample() {
            if (!this.dataFile)
                return;
            try {
                // uses httpretry so cannot be cancelled
                const file = await Project.getFileFromBlobStore(this.dataFile.id);
                if (!this.isAlive)
                    return;
                this.headers = file.headers;
                this.samples = file.samples;
                this.numResponses = file.num_responses;
                this.uploadFinished = true;
                this.$emit('file-ready', this.dataFile, this.headers, this.samples);
            }
            catch (errors) {
                if (!this.isAlive)
                    return;
                this.$store.dispatch(CLEAR_REQUEST_ERRORS); // use custom instead of global error handling
                this.uploadError = true;
            }
        },
        // Clear file and cached attributes
        resetFile() {
            this.periodicTask = null;
            this.dataFile = null;
            this.fileSelected = false;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = null;
            }
            this.fileProgress = undefined;
            this.uploadFinished = false;
            this.numResponses = null;
            this.$emit('file-cleared');
        },
        // Run the data tutorial
        showTutorial() {
            this.$emit('show-tutorial');
        },
        cancelUpload() {
            this.uploadRequest?.abort();
            this.checkFileStatusRequest?.abort();
            this.uploadRequest = null;
            this.checkFileStatusRequest = null;
            this.resetFile();
        },
        // Upload a file to the backend while updating progress indicator
        async uploadFile() {
            this.dataUnitsError = null;
            this.fileSizeError = null;
            this.fileTypeError = null;
            this.invalidFileType = false;
            this.uploadError = false;
            this.uploadErrorDetails = null;
            this.fileSelected = true;
            // bail if
            // - no selected file
            if (!this.$refs?.fileInput?.files?.length)
                return;
            // - no currentUser
            if (!this.currentUser)
                return;
            let file = this.$refs.fileInput.files[0];
            // Validate file extension
            this.fileName = file.name;
            if (!this.fileName.match(FILE_TYPE_RE)) {
                this.resetFile();
                this.invalidFileType = true;
                return;
            }
            this.$emit('file-selected');
            // Formatted file size
            this.fileSize = filesize(file.size);
            let data = new FormData();
            data.append('data', file);
            data.append('user', this.currentUser.id);
            try {
                this.fileProgress = 0;
                const response = await Project.uploadFile(data, progress => {
                    this.fileProgress = Math.round(progress.loaded / progress.total * 100);
                }, request => { this.uploadRequest = request; });
                if (!this.isAlive)
                    return;
                // Once the file is uploaded start checking for its status
                this.dataFile = response;
                this.periodicTask = setTimeout(this.checkFileStatus, TIMER_DELAY);
            }
            catch (maybeResponseOrError) {
                /*
                 if component is being torn down or has already been destroyed
                 or if the upload has been cancelled by the user
                 then errors don't matter so just return
                 */
                if (!this.isAlive || this.uploadRequest === null)
                    return;
                this.$emit('upload-failed');
                this.$store.dispatch(CLEAR_REQUEST_ERRORS); // use custom instead of global error handling
                const errorMessage = maybeResponseOrError.body?.data?.[0];
                if (errorMessage?.toLowerCase().startsWith('file exceeds')) {
                    this.fileSizeError = errorMessage;
                }
                else if (errorMessage?.toLowerCase().startsWith('unsupported file type')) {
                    this.fileTypeError = errorMessage;
                }
                else {
                    this.uploadError = true;
                    // capture unknown file error messages
                    if (!errorMessage) {
                        Sentry.setExtras({
                            'file upload progress': this.fileProgress,
                        });
                        Sentry.captureException(maybeResponseOrError);
                    }
                }
            }
            finally {
                this.uploadRequest = null;
            }
        },
        useIntegration() {
            this.$emit('use-integration');
        }
    }
});
export default DataUpload;
